/**
 * Comms-service client — apps/player's ONLY way to talk to the voice layer
 * (TOUCHLINE, Loop 5): the notifications centre (inbox + prefs + push) and the
 * Night Room. Mirrors lib/trustApi.ts law exactly:
 *
 * - Base URL: VITE_COMMS_URL (default http://localhost:8796).
 * - AUTH (D-009/D-010): every call carries `Authorization: Bearer
 *   <supabase_jwt>` off the stored auth session. No token ⇒
 *   CommsApiError("not_authenticated") BEFORE any network I/O.
 * - Request AND response are zod-validated against @matchday/contracts at the
 *   boundary — a drifted payload throws CommsApiError("bad_response"), never
 *   lands in the UI.
 * - Server errors (contracts ApiError shape) surface with the documented code
 *   VERBATIM (invalid_token · filtered · muted · room_closed · …), plus
 *   retry_after_s when the service sends one.
 *
 * Path map (COMMS SERVICE PATHS, brief):
 *   GET  /inbox                 → InboxView
 *   POST /inbox/{id}/read       → {ok:true}
 *   POST /inbox/{id}/acted      → {ok:true}
 *   GET  /prefs                 → PrefsView
 *   POST /prefs                 {UpdatePrefsRequest} → PrefsView
 *   POST /push/subscribe        {PushSubscribeRequest} → {ok:true}
 *   GET  /push/vapid-key        → {key}
 *   GET  /room/{night_code}     → RoomView (?after=cursor for the poll)
 *   POST /room/message          {PostRoomMessageRequest} → PostRoomMessageResponse
 *   POST /room/report           {ReportMessageRequest} → {ok:true}
 *
 * CONTRACT-CHANGE REQUEST FILED (see report): `UpdatePrefsRequest` carries no
 * quiet-hours field, yet `PrefsView.quiet_hours` is player-editable (TOUCHLINE
 * B3). `UpdateQuietHoursRequest` below is defined client-local (trustApi
 * `EvidenceRecordRequest` precedent) and POSTed to the same /prefs path —
 * ratify it in packages/contracts/src/comms.ts.
 */
import {
  ApiError,
  InboxView,
  PostRoomMessageRequest,
  PostRoomMessageResponse,
  PrefsView,
  PushSubscribeRequest,
  ReportMessageRequest,
  RoomView,
  UpdatePrefsRequest,
} from "@matchday/contracts";
import { z } from "zod";
import { auth } from "./platform";

export const COMMS_URL: string =
  (import.meta.env?.VITE_COMMS_URL as string | undefined) ?? "http://localhost:8796";

/** The `{ok:true}` acknowledgement the write paths answer with. */
export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;

/** GET /push/vapid-key — the public key PushManager.subscribe consumes. */
export const VapidKeyResponse = z.object({ key: z.string().min(1) });
export type VapidKeyResponse = z.infer<typeof VapidKeyResponse>;

/** POST /prefs quiet-hours variant — client-local pending contracts
 *  ratification (header note). */
export const UpdateQuietHoursRequest = z.object({ quiet_hours: z.boolean() });
export type UpdateQuietHoursRequest = z.infer<typeof UpdateQuietHoursRequest>;

export class CommsApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: unknown,
    public readonly retryAfterS?: number,
  ) {
    super(`comms api error ${status}: ${code}`);
    this.name = "CommsApiError";
  }
}

export interface CommsApiOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** The D-009 bearer source. Default: stored session's supabase_jwt. */
  getToken?: () => Promise<string | null>;
}

export interface CommsApi {
  /** GET /inbox — reverse-chron cards + the bell's actionable_unread. */
  getInbox(): Promise<InboxView>;
  /** POST /inbox/{id}/read — mark-read on view (B2). */
  markRead(notificationId: string): Promise<OkResponse>;
  /** POST /inbox/{id}/acted — the inline CTA fired. */
  markActed(notificationId: string): Promise<OkResponse>;
  /** GET /prefs — the B3 grid truth. */
  getPrefs(): Promise<PrefsView>;
  /** POST /prefs — one (category × channel) toggle; answers the fresh grid. */
  updatePref(req: UpdatePrefsRequest): Promise<PrefsView>;
  /** POST /prefs — quiet-hours variant (client-local schema, contract ask). */
  setQuietHours(req: UpdateQuietHoursRequest): Promise<PrefsView>;
  /** GET /push/vapid-key — public key for PushManager.subscribe. */
  getVapidKey(): Promise<VapidKeyResponse>;
  /** POST /push/subscribe — persist the browser's PushSubscription. */
  subscribePush(req: PushSubscribeRequest): Promise<OkResponse>;
  /** GET /room/{night_code} — the Night Room; `after` = the poll cursor. */
  getRoom(nightCode: string, after?: string | null): Promise<RoomView>;
  /** POST /room/message — slow-mode answers retry_in_seconds, not an error. */
  postRoomMessage(req: PostRoomMessageRequest): Promise<PostRoomMessageResponse>;
  /** POST /room/report — long-press report (3 taps max, A4). */
  reportMessage(req: ReportMessageRequest): Promise<OkResponse>;
}

async function sessionToken(): Promise<string | null> {
  const session = await auth.getSession();
  return session?.supabase_jwt ?? null;
}

export function createCommsApi(options: CommsApiOptions = {}): CommsApi {
  const base = (options.baseUrl ?? COMMS_URL).replace(/\/$/, "");
  const fetchImpl = options.fetchImpl ?? fetch;
  const getToken = options.getToken ?? sessionToken;

  async function throwApiError(res: Response): Promise<never> {
    let code = "unknown_error";
    let detail: unknown;
    let retryAfterS: number | undefined;
    try {
      const parsed = ApiError.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error;
        detail = parsed.data.detail;
        retryAfterS = parsed.data.retry_after_s;
      }
    } catch {
      // non-JSON error body — status + unknown_error is all we have
    }
    throw new CommsApiError(res.status, code, detail, retryAfterS);
  }

  async function call<T>(
    method: "GET" | "POST",
    path: string,
    body: unknown,
    schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  ): Promise<T> {
    const token = await getToken();
    if (token === null) throw new CommsApiError(0, "not_authenticated");
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers["content-type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await fetchImpl(`${base}${path}`, init);
    } catch {
      throw new CommsApiError(0, "network_error");
    }
    if (!res.ok) await throwApiError(res);
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new CommsApiError(res.status, "bad_response", "response body was not JSON");
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new CommsApiError(res.status, "bad_response", parsed.error.issues);
    }
    return parsed.data;
  }

  return {
    getInbox: () => call("GET", "/inbox", undefined, InboxView),
    markRead: (id) => call("POST", `/inbox/${encodeURIComponent(id)}/read`, {}, OkResponse),
    markActed: (id) => call("POST", `/inbox/${encodeURIComponent(id)}/acted`, {}, OkResponse),
    getPrefs: () => call("GET", "/prefs", undefined, PrefsView),
    updatePref: (req) => call("POST", "/prefs", UpdatePrefsRequest.parse(req), PrefsView),
    setQuietHours: (req) => call("POST", "/prefs", UpdateQuietHoursRequest.parse(req), PrefsView),
    getVapidKey: () => call("GET", "/push/vapid-key", undefined, VapidKeyResponse),
    subscribePush: (req) =>
      call("POST", "/push/subscribe", PushSubscribeRequest.parse(req), OkResponse),
    getRoom: (nightCode, after) =>
      call(
        "GET",
        `/room/${encodeURIComponent(nightCode)}${
          after !== undefined && after !== null ? `?after=${encodeURIComponent(after)}` : ""
        }`,
        undefined,
        RoomView,
      ),
    postRoomMessage: (req) =>
      call("POST", "/room/message", PostRoomMessageRequest.parse(req), PostRoomMessageResponse),
    reportMessage: (req) =>
      call("POST", "/room/report", ReportMessageRequest.parse(req), OkResponse),
  };
}

/** The app-wide instance (one client, one law). */
export const commsApi: CommsApi = createCommsApi();
